import { PUPPY_MAX_AGE_MS } from "@/features/discovery";
import type { SearchParams } from "@/features/discovery";
import { type FindFeedRowKey, findFeedRowDefinitions } from "../feed/find-feed";

export type { FindFeedRowKey };

export type FeedDescriptor = {
    type: "search" | "find-row";
    params: SearchParams;
    feedRowKey?: FindFeedRowKey;
    startsAtAnchor?: true;
};

export type DoomscrollSource = "adopt" | "explore" | "find";

const EXPLORE_PUPPY_CUTOFF_MS = Date.now() - PUPPY_MAX_AGE_MS;

type BaseSearch = {
    lat?: number;
    lng?: number;
    maxDistanceMiles?: number;
    dogId?: string;
    source?: DoomscrollSource;
};

export type FindRouteSearch = BaseSearch &
    Partial<SearchParams> & {
        feedRow?: FindFeedRowKey;
        q?: string;
    };

const FIND_ACTIVE_FILTER_KEYS = [
    "q",
    "hardRadius",
    "breedSlugs",
    "dogName",
    "breedType",
    "providerType",
    "size",
    "coatType",
    "sheddingLevel",
    "hypoallergenic",
    "priceMin",
    "priceMax",
    "dobMin",
    "dobMax",
    "sex",
    "friendlinessChildren",
    "friendlinessDogs",
    "friendlinessCats",
    "energyLevel",
    "trainability",
    "healthIssuesRisk",
    "bestMatchPreferences",
] as const satisfies readonly (keyof FindRouteSearch)[];

export function getResolvedLocation(search: BaseSearch) {
    if (search.lat === undefined || search.lng === undefined) {
        return null;
    }

    return {
        lat: search.lat,
        lng: search.lng,
    };
}

export function getCurrentDoomscrollReturnTo() {
    if (typeof window === "undefined") return undefined;

    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function getSafeDoomscrollReturnTo(raw: string | undefined) {
    if (!raw || typeof window === "undefined") return null;

    try {
        const url = new URL(raw, window.location.origin);
        if (url.origin !== window.location.origin) return null;

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}

export function buildExploreDoomscrollDescriptor(
    search: BaseSearch,
): FeedDescriptor | null {
    const resolvedLocation = getResolvedLocation(search);
    if (!resolvedLocation) return null;

    return {
        type: "search",
        params: {
            lat: resolvedLocation.lat,
            lng: resolvedLocation.lng,
            maxDistanceMiles: search.maxDistanceMiles,
            sortBy: "most-popular",
            dobMin: EXPLORE_PUPPY_CUTOFF_MS,
        },
    };
}

export function hasActiveFindFilters(search: FindRouteSearch): boolean {
    return FIND_ACTIVE_FILTER_KEYS.some((key) => search[key] !== undefined);
}

export function buildFindSearchParams(
    search: FindRouteSearch,
    nextParams: Partial<Omit<SearchParams, "page" | "limit" | "offset">> = {},
): SearchParams | undefined {
    const resolvedLocation = getResolvedLocation(search);
    if (!resolvedLocation) return undefined;

    return {
        lat: resolvedLocation.lat,
        lng: resolvedLocation.lng,
        maxDistanceMiles: search.maxDistanceMiles,
        locationSource: search.locationSource,
        ...nextParams,
    };
}

function buildFindRowDoomscrollDescriptor(
    search: FindRouteSearch,
    rowKey: FindFeedRowKey,
): FeedDescriptor | null {
    const rowDefinition = findFeedRowDefinitions.find(
        (definition) => definition.key === rowKey,
    );
    if (!rowDefinition) return null;

    const params = buildFindSearchParams(search, {
        ...rowDefinition.filters,
        feedContext: rowDefinition.title,
    });
    if (!params) return null;

    return {
        type: "find-row",
        feedRowKey: rowKey,
        params,
    };
}

export function buildFindResultsDoomscrollDescriptor(
    search: FindRouteSearch,
): FeedDescriptor | null {
    const resolvedLocation = getResolvedLocation(search);
    if (!resolvedLocation) return null;

    const {
        dogId: _dogId,
        source: _source,
        feedRow: _feedRow,
        ...params
    } = search;
    return {
        type: "search",
        params: {
            ...params,
            lat: resolvedLocation.lat,
            lng: resolvedLocation.lng,
        },
    };
}

export function buildCurrentFindDoomscrollDescriptor(
    search: FindRouteSearch,
): FeedDescriptor | null {
    if (search.feedRow) {
        return buildFindRowDoomscrollDescriptor(search, search.feedRow);
    }

    return hasActiveFindFilters(search)
        ? buildFindResultsDoomscrollDescriptor(search)
        : null;
}

export function resolveDiscoverFeedAnchor(input: {
    source?: "adopt" | "explore" | "find" | "feed";
    dogId?: string;
    tabFirstListingId: string | null;
}): {
    isTabMode: boolean;
    activeListingId: string | null;
    mode: "seeded" | "tab";
} {
    const requestedListingId = input.dogId ?? null;
    const isTabMode = input.source === "feed" && requestedListingId === null;
    return {
        isTabMode,
        activeListingId:
            requestedListingId ?? (isTabMode ? input.tabFirstListingId : null),
        mode: isTabMode ? "tab" : "seeded",
    };
}
